import { Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    FormFieldMessage,
    Input,
    Listbox,
    type ListboxItem,
} from '@/components/ui';
import type { OrgLevel } from '@/api/orgAdmin';
import type { InlineLevelPickerVM } from './useInlineLevelPicker';
import styles from './InlineLevelPicker.module.css';

/**
 * EAA-1817: three distinct empty states, and conflating them misleads. A row in a
 * descendant đơn vị loads that đơn vị's own levels on demand, so an empty list can
 * mean "still loading" — saying "this unit has no Level" while the request is in
 * flight sends the reader off to create one they already have.
 */
function emptyStateKey(picker: InlineLevelPickerVM): string {
    const base = 'console.hierarchy.scopedAccounts.inlineLevel.';
    if (picker.levelsLoading) return `${base}levelsLoading`;
    if (picker.query.trim()) return `${base}noLevelMatch`;
    return `${base}noLevelsAtAll`;
}

/**
 * EAA-1839 (frame 35a): the popover behind the table's amber "Chọn Level" chip.
 *
 * Anchored to the chip and not a modal, per the frame: the whole point is fixing a
 * row's empty Level without losing the row. See `useInlineLevelPicker` for the
 * write, the "áp dụng cho cả N" sweep, and why the frame's notify checkbox is
 * rendered inert.
 *
 * The frame also draws a second, "Kế thừa từ tổ chức cha", group of Levels. It is
 * NOT rendered: `GET /organizations/{id}/levels` returns that org's own rows only,
 * with no inherited set and no "cần duyệt" flag, so the group would either be empty
 * or invented. The rendered list is exactly the org's active Levels — the ones
 * `bulk-apply` will accept (`valid_level` is checked against that same set).
 */
export function InlineLevelPicker({
    picker,
    rowId,
    identityLabel,
    orgLabel,
    children,
}: {
    picker: InlineLevelPickerVM;
    /**
     * EAA-1839 (2026-08-25, user report: "error show id on title"): the person
     * this dialog is about, ALREADY resolved by the caller.
     *
     * The title used to read `row.user_email || row.user_sub` straight off the
     * assignment, and `user_email` is a denormalized snapshot that is legitimately
     * empty — so the heading rendered a raw Keycloak UUID
     * ("Đổi Level cho ab1a51e5-03bc-…") while the table two columns away showed
     * the person's real email, because the TABLE resolves identity through
     * `identityPrimaryOf` (display name → own email → realm-join email →
     * truncated sub). The dialog now takes that same resolved string instead of
     * re-deriving a worse one.
     */
    identityLabel?: string;
    /**
     * EAA-1817: the đơn vị this change writes into — `row.organization_id`, which for
     * a grouped account is the grant in the current scope. An account can hold grants
     * in several units, and the picker changes exactly ONE of them, so naming it is
     * the difference between a deliberate edit and a lucky one.
     */
    orgLabel?: string;
    /**
     * The row this instance hangs off. One `InlineLevelPicker` is rendered per Level
     * cell and only the one whose id matches the open target expands — anchoring a
     * single screen-level popover would have needed a DOM ref out of the table's
     * scroll container, which is exactly the descendant-reach CLAUDE.md forbids.
     */
    rowId: string;
    /** The chip the popover hangs off. */
    children: React.ReactNode;
}) {
    const { t } = useTranslation();
    const row = picker.row;
    const isOpen = row?.id === rowId;

    // EAA-1839: `picker.levels` is already the query-filtered set (see
    // `useInlineLevelPicker`), so this is a plain shape map onto the kit's item
    // type — no memo, because this component holds no other derived state and one
    // `InlineLevelPicker` is mounted per row with only the open one rendering.
    const levelItems: ListboxItem<OrgLevel>[] = picker.levels.map((level) => ({
        id: level.id,
        data: level,
    }));

    /* EAA-1839 (2026-08-25): the picker is reached two ways with two different
       meanings — the amber "Chọn Level" chip on a row that has none (assign), and
       the kebab's "Đổi Level" on a row that already has one (change). It said
       "Gán Level" either way, so arriving from "Đổi Level" landed on a dialog
       that named a different action than the menu item just clicked. */
    const isChange = Boolean(row?.level_name);

    const content = !isOpen ? null : (
        <div className={styles.pop}>
            {/* EAA-1839 (2026-08-25, user report: a newly created standard role did
                not appear here): this list is the ORGANIZATION'S LEVELS, not the
                standard-role vocabulary. Creating a "vai trò chuẩn" on
                /console/role-map adds a role KEY; it does not create a Level, so
                nothing shows up here until a Level carrying that key exists in
                this org. The two were indistinguishable on screen because a
                Level is usually NAMED after its role and the row prints the
                role key beside it — so the dialog now says which of the two it
                is listing. */}
            <p className={styles.title}>
                {t(
                    isChange
                        ? 'console.hierarchy.scopedAccounts.inlineLevel.titleChange'
                        : 'console.hierarchy.scopedAccounts.inlineLevel.title',
                    { identity: identityLabel || row?.user_email || row?.user_sub }
                )}
            </p>

            {/* Names what is being listed. Without it a reader who has just created
                a standard role reasonably expects to find it here. */}
            <p className={styles.subtitle}>
                {orgLabel
                    ? t('console.hierarchy.scopedAccounts.inlineLevel.targetUnit', {
                          org: orgLabel,
                      })
                    : t('console.hierarchy.scopedAccounts.inlineLevel.listSubtitle')}
            </p>

            <Input
                className={styles.search}
                value={picker.query}
                onChange={(event) => picker.setQuery(event.target.value)}
                placeholder={t('console.hierarchy.scopedAccounts.inlineLevel.searchPlaceholder')}
                aria-label={t('console.hierarchy.scopedAccounts.inlineLevel.searchPlaceholder')}
                allowClear
            />

            {/* EAA-1839: this was a `<label>` per option wrapping a raw
                `<input type="radio">` — a hand-written form control, which the
                conventions forbid outright when the kit ships an equivalent. It is
                the kit's `Listbox` now: single-select over a filtered list is exactly
                what that component models, and it brings the arrow/Home/End/Enter nav
                a radio list in a popover never had.

                The radio input becomes a DECORATIVE marker, because a real input
                cannot live inside a `role="option"` button and `aria-selected` already
                carries the state a radio group used to. It is drawn at both states, so
                the left gutter — and therefore the text alignment — is unchanged from
                the native control it replaces; the module's `.option` gap still
                spaces it, so this needed no CSS.

                Only the list moved. The popover shell, the cross-org `canApplyToAll`
                guard and the deliberately inert notify checkbox below are untouched. */}
            <Listbox<OrgLevel>
                className={styles.list}
                aria-label={t('console.hierarchy.scopedAccounts.inlineLevel.listAriaLabel')}
                items={levelItems}
                selectedId={picker.levelId || null}
                onSelect={(item) => picker.setLevelId(item.id)}
                optionsUnstyled
                optionClassName={(_item, state) =>
                    styles.option + (state.selected ? ' is-selected' : '')
                }
                emptyState={
                    <div className={styles.empty}>
                        {t(emptyStateKey(picker))}
                    </div>
                }
                renderItem={(item, state) => (
                    <>
                        <svg
                            viewBox="0 0 16 16"
                            width="16"
                            height="16"
                            aria-hidden="true"
                            /* The marker draws in `currentColor`, and the option
                               button is reset without one (see 06-ui-kit.css), so
                               the token is named here rather than inherited from
                               the UA `buttontext` the reset would otherwise leave. */
                            style={{
                                flex: 'none',
                                marginTop: 2,
                                color: 'var(--color-text-secondary)',
                            }}
                        >
                            <circle
                                cx="8"
                                cy="8"
                                r="7"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={1.4}
                                opacity={state.selected ? 1 : 0.45}
                            />
                            {state.selected && <circle cx="8" cy="8" r="3.6" fill="currentColor" />}
                        </svg>
                        <span className={styles.optionBody}>
                            <span className={styles.optionName}>
                                {item.data.name}
                                <code className={styles.optionRole}>{item.data.role_key}</code>
                            </span>
                            {item.data.description && (
                                <span className={styles.optionDesc}>
                                    {item.data.description}
                                </span>
                            )}
                        </span>
                    </>
                )}
            />

            <div className={styles.opts}>
                <Checkbox
                    checked={picker.applyToAll}
                    disabled={!picker.canApplyToAll}
                    title={
                        picker.canApplyToAll
                            ? undefined
                            : t('console.hierarchy.scopedAccounts.inlineLevel.applyAllCrossOrg')
                    }
                    onChange={(event) => picker.setApplyToAll(event.target.checked)}
                >
                    {t('console.hierarchy.scopedAccounts.inlineLevel.applyToAll', {
                        count: picker.noLevelCount ?? 0,
                    })}
                </Checkbox>
                <Checkbox
                    checked={false}
                    disabled
                    title={t('console.hierarchy.scopedAccounts.inlineLevel.notifyUnavailable')}
                >
                    {t('console.hierarchy.scopedAccounts.inlineLevel.notifyLabel')}
                </Checkbox>
            </div>

            <FormFieldMessage
                hint={t('console.hierarchy.scopedAccounts.inlineLevel.auditNote')}
                error={picker.error ?? undefined}
            />

            <div className={styles.footer}>
                <Button onClick={picker.close} disabled={picker.busy}>
                    {t('common.cancel')}
                </Button>
                <Button
                    type="primary"
                    loading={picker.busy}
                    disabled={!picker.levelId}
                    onClick={picker.submit}
                >
                    {t(
                        isChange
                            ? 'console.hierarchy.scopedAccounts.inlineLevel.submitButtonChange'
                            : 'console.hierarchy.scopedAccounts.inlineLevel.submitButton'
                    )}
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            open={isOpen}
            onOpenChange={(next) => {
                if (!next) picker.close();
            }}
            trigger="click"
            placement="bottomLeft"
            content={content}
            overlayClassName="sa-level-popover sa-level-popover--lg"
        >
            {/* antd's Popover clones its child to attach a ref, and the kit's `Chip`
                is a plain function component — so the trigger is wrapped in a span
                rather than handed the Chip directly, which would warn and lose the
                anchor. */}
            <span className={styles.anchor}>{children}</span>
        </Popover>
    );
}

export default InlineLevelPicker;
